<template>
  <div
    :class="{ 'mini-mode': mini }"
    class="price-range-container"
  >
    <div class="range-list">
      <!-- 左侧固定 -->
      <div class="fixed-column">
        <div class="cell">
          Quantity
        </div>
        <div
          v-if="!mini"
          class="cell"
        >
          Price
        </div>
        <div class="cell large">
          {{ shippingConfirmed ? 'PIFP' : 'Est.PIFP' }}
        </div>
      </div>

      <!-- 滚动区域 -->
      <div
        ref="scrollRef"
        class="scroll-container"
      >
        <div
          :style="{ width: list.length ? '' : '100%' }"
          class="scroll-inner"
        >
          <div
            v-for="(item, index) in list"
            :key="index"
            :class="['range-item', { active: index === activeIndex }]"
          >
            <img
              v-if="index === 1 && activeIndex === 1"
              class="choices-icon"
              src="@/assets/illus/illus-price-choices.svg"
              alt="active"
            >
            <div class="cell">
              {{ item.startQuantity }} Piece+
            </div>
            <div
              v-if="!mini"
              class="cell medium"
            >
              <IntlCurrency :currency="currencyData?.targetCurrency" />
              {{ bankersRoundWithMax(item.price * currencyData?.rate, 0.01) }}
            </div>
            <div class="cell large">
              <IntlCurrency :currency="currencyData?.targetCurrency" />
              {{ formatPifp(item) }}
            </div>
          </div>
        </div>
      </div>

      <!-- 安全区域 -->
      <div class="safe-offset" />
    </div>
    <div
      v-if="!mini"
      class="pifp-tips"
    >
      <div class="text">
        {{
          shippingConfirmed ?
            '*Per-item Fulfillment Price(PIFP). Calculated based on the minimum length, width, height and weight only. Please select the product you want to view the actual fulfillment price.'
            : '*Estimation Per-item Fulfillment Price(Est.PIFP). The length, width, height and weight are calculated by predicting the average value of similar products. The price will be verified again after adding it to the shopping cart. Please wait patiently.'
        }}
      </div>
      <Icon
        @click="showPopup = true"
        class="icon"
        name="icon-help-tip-l"
      />
    </div>

    <Popup
      v-model:show="showPopup"
      teleport="body"
      position="bottom"
      height="100%"
    >
      <div class="pifp-desc">
        <div class="header">
          {{ shippingConfirmed ? 'Per-item Fulfillment Price (PIFP)' : 'Estimation Per-item Fulfillment Price(Est.PIFP)' }}
          <img
            @click="showPopup = false"
            class="close-icon"
            src="@/assets/images/close-icon.svg"
            alt=""
          >
        </div>
        <div class="body">
          {{
            shippingConfirmed ?
              '*Calculated based on the minimum length, width, height and weight only. Please select the product you want to view the actual fulfillment price.'
              : '*The length, width, height and weight are calculated by predicting the average value of similar products. The price will be verified again after adding it to the shopping cart. Please wait patiently.'
          }}
        </div>
      </div>
    </Popup>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import Icon from '@/components/Icon/Icon.vue'
import { IntlCurrency } from '@/components/NewIntlCurrency'
import { bankersRoundWithMax } from '@/utils/utils'
import { Popup } from '@/components/Popup'
import { useCurrencyStore } from '@/pinia/currency'
import { useCalcShip } from '../../hooks/useCalcShip'

const props = defineProps({
  list: {
    type: Array,
    default: () => []
  },
  variants: {
    type: String,
    default: ''
  },
  openMini: {
    type: Boolean,
    default: false
  }
})

const { selectedProductCount, shippingConfirmed } = useCalcShip()

const currencyStore = useCurrencyStore()

const activeIndex = ref(props.list?.length < 2 ? 0 : 1)

const scrollRef = ref(null)

const showPopup = ref(false)

const mini = ref(false)

const currencyData = computed(() => currencyStore.current)

watch(selectedProductCount, (value) => {
  if (value) {
    setActive(value)
  } else {
    activeIndex.value = props.list.length > 1 ? 1 : 0
  }
})

watch(() => props.list, (list) => {
  if (list.length > 1) {
    setActive(selectedProductCount.value)
  } else {
    activeIndex.value = 0
  }
})

const formatPifp = (item) => {
  const targetPrice = +bankersRoundWithMax(item.price * currencyData.value.rate, 0.01)
  const v = (item.shipAmount + targetPrice * item.startQuantity) / item.startQuantity
  return v.toFixed(2)
}

const setActive = (total) => {
  const el = scrollRef.value

  /**
   * 不满足最小起订量的情况下，选中第一个价格区间
   */
  activeIndex.value = 0
  props.list.forEach((item, index) => {
    if (total >= item.startQuantity) {
      activeIndex.value = index
      if (index === 0) {
        el.scrollTo({
          left: 0,
          behavior: 'smooth'
        })
      }
      if (index >= 2) {
        el.scrollTo({
          left: el.scrollWidth,
          behavior: 'smooth'
        })
      }
    }
  })
}

let startY = 0
onMounted(() => {
  document.addEventListener('touchstart', (e) => {
    startY = e.touches[0].clientY
  })

  document.addEventListener('touchend', (e) => {
    if (!props.openMini) return
    const endY = e.changedTouches[0].clientY
    const delta = endY - startY

    if (delta > 10) {
      mini.value = false
    } else if (delta < -10) {
      mini.value = true
    }
  })
})

defineExpose({
  activeIndex
})
</script>

<style scoped lang="less">
.price-range-container {
  padding: 0 12px;
  margin-top: 12px;

  &.mini-mode {
    .range-list {
      &::after {
        display: none;
      }
    }
    .fixed-column {
      height: 92px !important;
    }
    .scroll-container {
      height: 106px !important;
      .scroll-inner {
        height: 92px !important;
        .range-item {
          height: 92px !important;
        }
      }
    }
    .safe-offset {
      height: 92px !important;
    }
  }

  .range-list {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;

    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      height: 0.5px;
      background-color: #dfdfdf;
      z-index: 9;
    }

    &::before {
      top: 50px;
    }

    &::after {
      top: 92px;
    }

    .safe-offset {
      position: absolute;
      right: 0;
      width: 4px;
      height: 136px;
      background-color: #eff4fd;
      z-index: 1;
      border-radius: 0 4px 4px 0;
      transition: height .3s;
    }

    .fixed-column {
      width: 72px;
      height: 136px;
      padding: 4px;
      box-shadow: 6px 0 10px -4px rgba(0, 0, 0, 0.1);
      background: #eff4fd;
      border-radius: 4px 0 0 4px;
      flex-shrink: 0;
      position: relative;
      z-index: 2;
      transition: height .3s;

      .cell {
        height: 44px;
        font-size: 12px;
        font-family: mon-600;
        color: #232323;
        display: flex;
        align-items: center;
        justify-content: center;
        &:nth-child(1) {
          height: 40px;
        }

        &.large {
          .icon {
            margin-left: 2px;
            cursor: pointer;
          }
        }
      }
    }

    .scroll-container {
      width: calc(100% - 76px);
      height: 148px;
      overflow-x: auto;
      overflow-y: hidden;
      position: relative;
      display: flex;
      align-items: center;
      transition: height .3s;

      &::-webkit-scrollbar {
        display: none;
      }

      .scroll-inner {
        display: flex;
        position: relative;
        overflow: visible;
        height: 136px;
        background: #eff4fd;
        padding: 0 4px;
        transition: height .3s;
      }

      .range-item {
        position: relative;
        flex-shrink: 0;
        height: 136px;
        padding: 4px 0;
        background: #eff4fd;
        cursor: pointer;
        transition: height .3s;

        .cell {
          width: 96px;
          height: 44px;
          font-size: 12px;
          font-family: mon-500;
          color: #232323;
          display: flex;
          align-items: center;
          justify-content: center;

          &:nth-child(1) {
            height: 40px;
          }

          &.medium {
            font-size: 14px;
            font-family: mon-700;
          }

          &.large {
            font-size: 16px;
            font-family: mon-700;
          }
        }

        &.active {
          top: 0;
          background: #dfe8fb;
          height: 136px;
          outline: 2px solid #052784;
          outline-offset: -2px;
          border-radius: 8px;
          z-index: 10;

          .cell {
            color: #052784;
          }
          .cell:first-of-type {
            height: 40px;
          }
        }

        .choices-icon {
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%);
        }
      }
    }

    .scroll-button {
      position: absolute;
      top: 52px;
      right: -10px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04);
      z-index: 99;

      .icon {
        font-size: 12px;
        transition: transform 0.3s ease;
      }

      .rotate {
        transform: rotate(180deg);
      }
    }
  }

  .pifp-tips {
    display: flex;
    align-items: center;
    margin-top: 2px;
    .text {
      flex: 1;
      color: #6e717a;
      font-size: 11px;
      font-family: mon-500;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .icon {
      font-size: 16px;
      color: #232323;
      cursor: pointer;
    }
  }
}

.pifp-desc {
  position: absolute;
  bottom: 0;
  background-color: white;
  border-radius: 16px 16px 0 0;
  padding-top: 8px;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    padding: 0 12px;
    color: #232323;
    font-size: 14px;
    font-family: mon-700;

    .close-icon {
      width: 24px;
      height: 24px;
    }
  }
  .body {
    margin-top: 16px;
    margin-bottom: 48px;
    padding: 0 12px;
    color: #232323;
    font-size: 12px;
    font-family: mon-500;
    line-height: 16px;
  }
}
</style>
